import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import TrimbleMapsDraw from "@trimblemaps/trimblemaps-draw";
import type { DrawCreateEvent, DrawDeleteEvent, DrawUpdateEvent } from "@trimblemaps/trimblemaps-draw";
import { memo, useEffect, useRef } from "react";
import type { Polygon } from "@trimblemaps/trimblemaps-js/geojson";

type Props = {
  value?: Polygon,
  onChange: (shape: Polygon | null) => void,
  style?: React.CSSProperties
};

const GeofenceMap = ({ value, onChange, style }: Props) => {
  const map = useRef<TrimbleMaps.Map>(null);

  useEffect(() => {
    const style = localStorage["darkmode"] === 'false' ? TrimbleMaps.Common.Style.TRANSPORTATION : TrimbleMaps.Common.Style.TRANSPORTATION_DARK;
    map.current = new TrimbleMaps.Map({
      container: "draw-map",
      style: style,
      center: [-98.5795, 39.8283],
      zoom: 3,
      attributionControl: false,
    });

    map.current.addControl(new TrimbleMaps.NavigationControl());
    map.current.addControl(new TrimbleMaps.ScaleControl({ unit: 'imperial' }));
    map.current.addControl(new TrimbleMaps.FullscreenControl());
    map.current.setDarkMode(style === TrimbleMaps.Common.Style.TRANSPORTATION_DARK);

    const drawControl = new TrimbleMapsDraw({
      controls: { polygon: true, rectangle: true, trash: true },
      displayControlsDefault: false,
      defaultMode: 'draw_polygon',
    });

    // Map#addControl takes an optional second argument to set the position of the control.
    // If no position is specified the control defaults to `top-right`.
    map.current.addControl(drawControl, "top-left");

    if (value) {
      drawControl.add(value);

      const bounds = new TrimbleMaps.LngLatBounds();
      value.coordinates[0].forEach((c) => {
        if (Array.isArray(c) && c.length === 2) {
          bounds.extend(c as [number, number]);
        }
      });
      map.current.fitBounds(bounds, { padding: 50 });
    }

    map.current.on("draw.create", (e: DrawCreateEvent) => {

      // Remove all other features
      const feature = drawControl.getAll();
      feature.features.forEach(f => {
        if (f.id !== e.features[0].id) {
          drawControl.delete(f.id.toString());
        }
      });

      // Get the geometry of the created feature
      const geometry = e.features[0].geometry;
      if (geometry.type === 'Polygon') {
        onChange(geometry);
      }
    });

    map.current.on("draw.update", (e: DrawUpdateEvent) => {

      // Get the geometry of the updated feature
      const geometry = e.features[0].geometry;
      if (geometry.type === 'Polygon') {
        onChange(geometry);
      }
    });

    map.current.on("draw.delete", (_e: DrawDeleteEvent) => {
      onChange(null);
    });

    return () => {
      map.current.remove();
    }
  }, [onChange, value]);

  return <div id="draw-map" style={{ width: '100%', height: '100%', ...style }} />;
}

export default memo(GeofenceMap, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});